import React, { useState, useEffect } from 'react';
import { FiCheck, FiX, FiArrowRight, FiHelpCircle } from 'react-icons/fi';

/**
 * FlashcardStack - Componente de flashcard com efeito de pilha e animação de flip 3D
 *
 * Features:
 * - Stack visual: cartas empilhadas atrás da carta atual
 * - Flip 3D: animação suave ao revelar resposta/feedback
 * - Autoavaliação: o aluno diz se lembrou ou não, sem alternativas de múltipla escolha
 * - Transições suaves entre cartas
 */
export function FlashcardStack({ question, correctAnswer, explanation, onAnswer, onContinue, onClose, isLoading, questionNumber, totalCards, showFeedback, isCorrect, isLastCard, correctCount = 0, wrongCount = 0, nextQuestion = '' }) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isEntering, setIsEntering] = useState(false);

  // Reset estados quando mudar de carta
  useEffect(() => {
    if (!showFeedback) {
      setIsFlipped(false);
      setSelectedAnswer(null);
      // Trigger entrada da nova carta
      if (isEntering) {
        setTimeout(() => setIsEntering(false), 400);
      }
    }
  }, [showFeedback, question]);

  // Flip para mostrar feedback
  useEffect(() => {
    if (showFeedback && !isFlipped) {
      setIsAnimating(true);
      setTimeout(() => {
        setIsFlipped(true);
        setTimeout(() => setIsAnimating(false), 400);
      }, 100);
    }
  }, [showFeedback]);

  const handleSelectAnswer = remembered => {
    if (isLoading || isFlipped || selectedAnswer !== null) return;
    setSelectedAnswer(remembered);
    onAnswer(remembered);
  };

  const handleContinue = () => {
    if (isAnimating || isExiting) return;
    setIsAnimating(true);
    setIsExiting(true);

    // Espera a animação de saída completa (card sai virado)
    setTimeout(() => {
      // Agora que a carta saiu da tela, reseta os estados
      setIsExiting(false);
      setIsFlipped(false);
      setSelectedAnswer(null);
      setIsEntering(true);
      onContinue();

      // Pequeno delay antes de mostrar a nova carta entrando
      setTimeout(() => {
        setIsAnimating(false);
        setIsEntering(false);
      }, 400);
    }, 400); // Aumentado para garantir que a animação de saída complete
  };

  // Calcula quantas cartas mostrar na pilha
  const remainingCards = totalCards - questionNumber;
  const stackCards = Math.min(remainingCards, 3);

  return (
    <div className={`flashcard-stack ${isExiting ? 'flashcard-stack--swapping' : ''} ${isEntering ? 'flashcard-stack--entering' : ''}`}>
      {/* Card de trás com preview da próxima pergunta */}
      {/* {nextQuestion && (
        <div className="flashcard-stack__next-card">
          <div className="flashcard-card flashcard-card--preview">
            <div className="flashcard-card__header">
              <span className="flashcard-card__number">
                <FiHelpCircle className="flashcard-card__icon" />
                Questão {questionNumber + 1}
              </span>
            </div>
            <div className="flashcard-card__question">
              <h3>{nextQuestion}</h3>
            </div>
            <div className="flashcard-card__alternatives flashcard-card__alternatives--preview">
              {['A', 'B', 'C', 'D'].map(letter => (
                <div
                  key={letter}
                  className="flashcard-card__alternative flashcard-card__alternative--placeholder"
                >
                  <span className="flashcard-card__alternative-letter">{letter}</span>
                  <span className="flashcard-card__alternative-text flashcard-card__alternative-text--placeholder" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )} */}
      {/* Carta principal com flip */}
      <div className={`flashcard-flip ${isFlipped ? 'flashcard-flip--flipped' : ''} ${isAnimating ? 'flashcard-flip--animating' : ''} ${isExiting ? 'flashcard-flip--exiting' : ''} ${isEntering ? 'flashcard-flip--entering' : ''}`}>
        {/* Frente da carta - Pergunta */}
        <div className="flashcard-flip__face flashcard-flip__front">
          <div className="flashcard-card">
            {/* Barra de progresso no topo do card */}
            <div className="flashcard-card__progress">
              <div
                className="flashcard-card__progress-fill"
                style={{ width: `${(questionNumber / totalCards) * 100}%` }}
              />
            </div>

            <div className="flashcard-card__header">
              <span className="flashcard-card__number">
                <FiHelpCircle className="flashcard-card__icon" />
                Questão {questionNumber}
              </span>
              <div className="flashcard-card__score">
                <span className="flashcard-card__score-correct">✓ {correctCount}</span>
                <span className="flashcard-card__score-wrong">✗ {wrongCount}</span>
              </div>
            </div>

            <div className="flashcard-card__question">
              <h3>{question}</h3>
            </div>

            <div className="flashcard-card__recall-actions">
              <button
                type="button"
                className={`flashcard-card__recall-btn flashcard-card__recall-btn--know ${selectedAnswer === true ? 'flashcard-card__recall-btn--selected' : ''}`}
                onClick={() => handleSelectAnswer(true)}
                disabled={isLoading || selectedAnswer !== null}
              >
                <FiCheck className="flashcard-card__recall-icon" />
                Lembrei
              </button>
              <button
                type="button"
                className={`flashcard-card__recall-btn flashcard-card__recall-btn--dont-know ${selectedAnswer === false ? 'flashcard-card__recall-btn--selected' : ''}`}
                onClick={() => handleSelectAnswer(false)}
                disabled={isLoading || selectedAnswer !== null}
              >
                <FiX className="flashcard-card__recall-icon" />
                Não lembrei
              </button>
            </div>
          </div>
        </div>

        {/* Verso da carta - Feedback */}
        <div className="flashcard-flip__face flashcard-flip__back">
          <div className={`flashcard-card flashcard-card--back ${isCorrect ? 'flashcard-card--correct' : 'flashcard-card--wrong'}`}>
            <div className="flashcard-card__feedback-icon">
              {isCorrect ? (
                <div className="flashcard-card__icon-circle flashcard-card__icon-circle--correct">
                  <FiCheck className="flashcard-card__icon-svg" />
                </div>
              ) : (
                <div className="flashcard-card__icon-circle flashcard-card__icon-circle--wrong">
                  <FiX className="flashcard-card__icon-svg" />
                </div>
              )}
            </div>

            <div className="flashcard-card__feedback-content">
              <h3 className="flashcard-card__feedback-title">{isCorrect ? 'Mandou bem!' : 'Vamos revisar essa'}</h3>

              <div className="flashcard-card__correct-answer">
                <span className="flashcard-card__correct-label">Resposta:</span>
                <span className="flashcard-card__correct-text">{correctAnswer}</span>
              </div>

              {explanation && (
                <div className="flashcard-card__explanation">
                  <span className="flashcard-card__explanation-label">Explicação:</span>
                  <p className="flashcard-card__explanation-text">{explanation}</p>
                </div>
              )}
            </div>

            <button
              type="button"
              className="flashcard-card__continue-btn"
              onClick={handleContinue}
              disabled={isAnimating}
            >
              {isLastCard ? 'Ver Resultados' : 'Próxima Questão'}
              <FiArrowRight className="flashcard-card__continue-icon" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FlashcardStack;
